import type {NotificationFilter, NotificationsResponse, NotificationTopic} from './types';

import {Chip, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import Empty from 'common/empty/Empty';
import {useCallback, useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useAppDispatch, useAppSelector} from 'store';
import {listNotifications, viewNotifications} from 'store/slices/notification/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import NotificationItem from './components/NotificationItem/NotificationItem';

const NOTIFICATION_PAGE_SIZE = 10;

const NOTIFICATION_FILTERS: NotificationFilter[] = [
  {title: 'همه', value: 'all'},
  {title: 'ترقی', value: 'promotion_center'},
  {title: 'امتیاز عملکرد', value: 'vps'},
  {title: 'اعلان‌ها', value: 'notification'},
  {title: 'تیکت‌ها', value: 'ticket'}
];

const NotificationCenter: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const notifications = useAppSelector(
    (state) => state.notification.notifications
  ) as NotificationsResponse | null;
  const loading = useAppSelector((state) => state.notification.loading) as number;
  const [selectedTopic, setSelectedTopic] = useState<NotificationTopic>('all');
  const [pageNumber, setPageNumber] = useState(1);
  const messages = useMemo(
    () => (Array.isArray(notifications?.messages) ? notifications.messages : []),
    [notifications?.messages]
  );

  const getNotifications = useCallback(
    (page: number, topic: NotificationTopic) => {
      void dispatch(
        listNotifications({
          vendor_id: vendorId,
          page,
          page_size: NOTIFICATION_PAGE_SIZE,
          topic
        })
      );
    },
    [dispatch, vendorId]
  );

  const loadMore = () => {
    const nextPage = pageNumber + 1;
    getNotifications(nextPage, selectedTopic);
    setPageNumber(nextPage);
  };

  useEffect(() => {
    setPageNumber(1);
    getNotifications(1, selectedTopic);
  }, [getNotifications, selectedTopic]);

  useEffect(() => {
    const unseenNotificationIds = messages
      .filter((notification) => !notification.seen_at)
      .map(({id}) => id);

    if (unseenNotificationIds.length) {
      void dispatch(viewNotifications({vendor_id: vendorId, ids: unseenNotificationIds}));
    }
  }, [dispatch, messages, vendorId]);

  const isInitialLoading = loading > 0 && messages.length === 0;

  return (
    <PageLayout
      contentId="notification-center-list"
      header={
        <div
          className="flex min-h-12.5 items-center gap-2.5 overflow-x-auto border-b border-gray-02 p-2.5"
          aria-label="فیلتر پیام‌ها"
        >
          {NOTIFICATION_FILTERS.map((filter) => (
            <Chip
              key={filter.value}
              label={filter.title}
              variant="outline"
              selected={selectedTopic === filter.value}
              onClick={() => setSelectedTopic(filter.value)}
            />
          ))}
        </div>
      }
    >
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="medium" color="primary" />
        </div>
      ) : messages.length > 0 ? (
        <InfiniteScroll
          scrollableTarget="notification-center-list"
          hasMore={messages.length < (notifications?.total ?? 0)}
          dataLength={messages.length}
          next={loadMore}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
          scrollThreshold={0.6}
        >
          {messages.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              vendorId={vendorId}
            />
          ))}
        </InfiniteScroll>
      ) : (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Empty>
            <p className="text-body-sm text-neutral-fg">هیچ اطلاعیه‌ای یافت نشد</p>
          </Empty>
        </div>
      )}
    </PageLayout>
  );
};

export default NotificationCenter;
